import { type ReactNode } from "react";
import { faIdCard } from "@fortawesome/pro-light-svg-icons";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { T } from "@repo/transifex";
import { type Till } from "~/api/tills/types";
import { Tile, TileIcon } from "~/components/home/tile";
import { KioskLogo } from "~/components/home/images/kiosk-logo";
import { SellLogo } from "~/components/home/images/sell-logo";
import { CollectLogo } from "~/components/home/images/collect-logo";
import { TicketScannerLogo } from "~/components/home/images/ticket-scanner-logo";
import { basketActions } from "~/providers/store/basket";
import { authActions } from "~/providers/store/auth";
import { sessionsKeys } from "~/api/sessions/keys";
import { getSession } from "~/api/sessions/requests";
import { isSessionStartedToday } from "~/api/sessions/is-session-started-today";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";

type MainTileProps = {
  till: Till;
  isTillOpen: boolean;
  isSessionValid: boolean;
};

export const MainTile = ({
  till,
  isTillOpen,
  isSessionValid
}: MainTileProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetch: refetchTill } = useCurrentTill();

  const warning = getTileWarning(isTillOpen, isSessionValid);

  // The cached till/session drive the warning badge, but they may be stale (a
  // session can be closed/opened on another device). So we only confirm with
  // fresh data on click — and only when the cache already flags a problem —
  // diverting to the open/close screen only if fresh data agrees. The tile is
  // disabled while this resolves; the happy path does no network.
  const navigateTo = useMutation({
    mutationFn: async ({
      route,
      unattended = false
    }: {
      route: string;
      unattended?: boolean;
    }) => {
      if (isTillOpen && isSessionValid) {
        return { action: "navigate", route, unattended } as const;
      }

      const { data: freshTill } = await refetchTill();
      const sessionId = freshTill?.sessionId;

      if (!freshTill || !sessionId) {
        return { action: "open", tillId: freshTill?.id ?? till.id } as const;
      }

      const session = await queryClient.fetchQuery({
        queryKey: sessionsKeys.detail({ sessionId, tillId: freshTill.id }),
        queryFn: () => getSession({ sessionId, tillId: freshTill.id })
      });

      if (!isSessionStartedToday(session.startedAt, freshTill.timezone)) {
        return { action: "close", tillId: freshTill.id, sessionId } as const;
      }

      return { action: "navigate", route, unattended } as const;
    },
    onSuccess: (result) => {
      switch (result.action) {
        case "open":
          navigate(`/settings/tills/${result.tillId}/open`);
          break;
        case "close":
          navigate(
            `/settings/tills/${result.tillId}/${result.sessionId}/close`
          );
          break;
        case "navigate":
          if (result.unattended) {
            authActions.setUnattended();
          }
          navigate(result.route);
          break;
      }
    }
  });

  switch (till.type) {
    case "kiosk":
      return (
        <Tile
          onClick={() => {
            navigateTo.mutate({ route: "/kiosk", unattended: true });
          }}
          isLoading={navigateTo.isPending}
          variant="main"
          title={<T _str="Kiosk" />}
          warning={warning}
          className="min-w-0 flex-1"
        >
          <KioskLogo />
        </Tile>
      );

    case "ticket-validation":
      return (
        <Tile
          onClick={() => {
            navigateTo.mutate({
              route: "/ticket-validation",
              unattended: true
            });
          }}
          isLoading={navigateTo.isPending}
          variant="main"
          title={<T _str="Ticket scanner" />}
          warning={warning}
          className="min-w-0 flex-1"
        >
          <TicketScannerLogo />
        </Tile>
      );

    case "simple-checkout":
      return (
        <Tile
          onClick={() => {
            navigateTo.mutate({ route: "/simple-checkout", unattended: true });
          }}
          isLoading={navigateTo.isPending}
          variant="main"
          title={<T _str="Simple checkout" />}
          warning={warning}
          className="min-w-0 flex-1"
        >
          <TileIcon icon={faIdCard} />
        </Tile>
      );

    case "self-service":
      return (
        <Tile
          onClick={() => {
            navigateTo.mutate({ route: "/pos", unattended: true });
          }}
          disabled={navigateTo.isPending}
          variant="main"
          title={<T _str="Sell" />}
          warning={warning}
          className="min-w-0 flex-1"
        >
          <SellLogo />
        </Tile>
      );
    case "attended":
      return (
        <Tile
          onClick={() => {
            navigateTo.mutate({ route: "/pos" });
          }}
          isLoading={navigateTo.isPending}
          variant="main"
          title={<T _str="Sell" />}
          warning={warning}
          className="min-w-0 flex-1"
        >
          <SellLogo />
        </Tile>
      );
    case "collect": {
      const hasNoCollectionModules = till.config.collectionModules.length === 0;

      return (
        <div className="flex flex-1 flex-col gap-4 landscape:flex-row">
          <Tile
            onClick={() => {
              basketActions.reset();
              navigateTo.mutate({ route: "/collect" });
            }}
            isLoading={navigateTo.isPending}
            variant="main"
            title={<T _str="Collect" />}
            warning={
              hasNoCollectionModules ? (
                <T _str="The modules linked to the Collect till cannot be found. Please review this till's setup in the Admin panel" />
              ) : (
                warning
              )
            }
            disabled={hasNoCollectionModules}
            className="min-w-0 flex-1"
          >
            <CollectLogo />
          </Tile>

          <Tile
            onClick={() => {
              navigateTo.mutate({ route: "/pos" });
            }}
            isLoading={navigateTo.isPending}
            variant="main"
            title={<T _str="POS" />}
            warning={warning}
            className="min-w-0 flex-1"
          >
            <SellLogo />
          </Tile>
        </div>
      );
    }

    case "collect-lite": {
      const hasNoCollectionModules = till.config.collectionModules.length === 0;

      return (
        <Tile
          onClick={() => {
            navigate("/collect");
          }}
          variant="main"
          title={<T _str="Classroom Ordering" />}
          warning={
            hasNoCollectionModules ? (
              <T _str="The modules linked to the Classroom Ordering till cannot be found. Please review this till's setup in the Admin panel" />
            ) : undefined
          }
          disabled={hasNoCollectionModules}
          className="min-w-0 flex-1"
        >
          <CollectLogo />
        </Tile>
      );
    }
    case "external":
    default:
      return null;
  }
};

function getTileWarning(
  isTillOpen: boolean,
  isSessionValid: boolean
): ReactNode {
  if (!isTillOpen) {
    return <T _str="Open the till to continue" />;
  }
  if (!isSessionValid) {
    return <T _str="The till needs to be closed from yesterday" />;
  }
  return undefined;
}
